import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertIcon,
  Button,
  HStack,
  Text,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useColorModeValue,
  Spinner,
} from '@chakra-ui/react';
import { Clock, RefreshCw } from 'lucide-react';
import { useTokenManager, useTokenCountdown } from '../../hooks/useTokenManager';

interface SessionExpiryWarningProps {
  isOpen?: boolean;
  onClose?: () => void;
  variant?: 'modal' | 'alert' | 'compact';
}

const SessionExpiryWarning: React.FC<SessionExpiryWarningProps> = ({
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  variant = 'modal'
}) => {
  const { t } = useTranslation();
  const {
    isRefreshing, 
    showWarning, 
    extendSession, 
    dismissWarning 
  } = useTokenManager();
  
  const { countdown, isExpiringSoon } = useTokenCountdown();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  
  // Use external control if provided, otherwise use internal logic
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : showWarning;
  const onClose = externalOnClose || dismissWarning;

  if (!isExpiringSoon && !showWarning) {
    return null;
  }

  const handleExtendSession = async () => {
    await extendSession();
    onClose();
  };

  if (variant === 'alert') {
    return (
      <Alert status="warning" borderRadius="lg" mb={4}>
        <AlertIcon />
        <VStack align="start" spacing={1} flex={1}>
          <Text fontWeight="medium" fontSize="sm">
            Your session will expire soon
          </Text>
          <Text fontSize="xs" color="orange.600">
            Time remaining: {countdown}
          </Text>
        </VStack>
        <HStack spacing={2}>
          <Button
            size="sm"
            variant="outline"
            onClick={onClose}
          >
            Dismiss
          </Button>
          <Button
            size="sm"
            colorScheme="orange"
            onClick={handleExtendSession}
            isLoading={isRefreshing}
            leftIcon={isRefreshing ? <Spinner size="xs" /> : <RefreshCw size={12} />}
          >
            Extend
          </Button>
        </HStack>
      </Alert>
    );
  }

  if (variant === 'compact') {
    return (
      <HStack 
        spacing={2} 
        bg="orange.50" 
        p={2} 
        borderRadius="md" 
        border="1px solid"
        borderColor="orange.200"
      >
        <Clock size={16} color="orange" />
        <Text fontSize="xs" color="orange.700" fontWeight="medium">
          {t('qr.sessionExpiresIn', { time: countdown })}
        </Text>
        <Button
          size="xs"
          variant="ghost"
          colorScheme="orange"
          onClick={handleExtendSession}
          isLoading={isRefreshing}
        >
          {t('qr.extend')}
        </Button>
      </HStack>
    );
  }

  // Modal variant (default)
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent bg={bgColor}>
        <ModalHeader>
          <HStack spacing={3}>
            <Clock size={24} color="orange" />
            <Text color={textColor}>{t('qr.sessionExpiringTitle')}</Text>
          </HStack>
        </ModalHeader>

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Alert status="warning" borderRadius="lg">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <Text fontWeight="medium" fontSize="sm">
                  {t('qr.willExpireIn', { time: countdown })}
                </Text>
                <Text fontSize="sm">
                  {t('qr.willLogOut')}
                </Text>
              </VStack>
            </Alert>

            <Text fontSize="sm" color={textColor}>
              {t('qr.extendQuestion')}
            </Text>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="outline"
              onClick={onClose}
              isDisabled={isRefreshing}
            >
              {t('qr.dismiss')}
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleExtendSession}
              isLoading={isRefreshing}
              loadingText={t('qr.extending')}
              leftIcon={!isRefreshing ? <RefreshCw size={16} /> : undefined}
            >
              {t('qr.extendSession')}
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SessionExpiryWarning;